import { Link } from "@tanstack/react-router";
import {
  type Cell,
  flexRender,
  type Header,
  type Row,
} from "@tanstack/react-table";
import { ArrowDownIcon, ArrowUpIcon } from "lucide-react";
import prettyBytes from "pretty-bytes";
import { twMerge } from "tailwind-merge";

import { useMediaPreviewModal } from "@/components/media-preview-modal/useMediaPreviewModal";
import AssetFolder from "@/features/my-files/assets/asset-folder.svg?react";
import { useIsMobileScreen } from "@/hooks/useIsMobileScreen";

import type { FileItem } from "../../types";

import { FILE_ICONS } from "../../assets/file-icons";
import { ActionMenu } from "../action-menu";

const getColumnStyles = (columnId: string) => {
  if (columnId === "name" || columnId === "mobileName") {
    return {
      flex: "1 1 auto",
      minWidth: "0",
      width: "auto",
    };
  }

  if (columnId === "mobileSelect") {
    return {
      alignContent: "start",
      flex: "0 0 50px",
      width: "50px",
    };
  }

  if (columnId === "select") {
    return {
      flex: "0 0 50px",
      width: "50px",
    };
  }

  if (columnId === "size") {
    return {
      flex: "0 0 96px",
      width: "96px",
    };
  }

  if (columnId === "createdAt") {
    return {
      flex: "0 0 146px",
      width: "146px",
    };
  }

  return {
    flex: "0 0 100px",
    width: "100px",
  };
};

function SortingIndicator({ header }: { header: Header<FileItem, unknown> }) {
  if (!header.column.getCanSort()) return null;

  const sortDirection = header.column.getIsSorted() as string;

  if (!sortDirection) return null;

  return (
    <span>
      {sortDirection === "asc" ? (
        <ArrowDownIcon className="text-fg-tertiary" size={16} />
      ) : (
        <ArrowUpIcon className="text-fg-tertiary" size={16} />
      )}
    </span>
  );
}

function FileIcon({ fileItem }: { fileItem: FileItem }) {
  if (fileItem.type === "file") {
    if (fileItem.extension in FILE_ICONS) {
      const IconComponent =
        FILE_ICONS[fileItem.extension as keyof typeof FILE_ICONS];

      return <IconComponent className="size-2xl md:size-3xl shrink-0" />;
    } else {
      const IconComponent = FILE_ICONS.fallback;

      return <IconComponent className="size-2xl md:size-3xl shrink-0" />;
    }
  }

  return <AssetFolder className="shrink-0" />;
}

function FileCellContent({
  cell,
  row,
}: {
  cell: Cell<FileItem, unknown>;
  row: Row<FileItem>;
}) {
  const isMobile = useIsMobileScreen();
  const { openModal } = useMediaPreviewModal();

  return (
    <div
      className="flex items-start md:items-center gap-lg cursor-pointer"
      onClick={() => {
        if (row.original.type === "file") {
          openModal(String(row.original.id));
        }
      }}
      style={getColumnStyles(cell.column.id)}
    >
      <FileIcon fileItem={row.original} />
      <span className="truncate text-xs md:text-sm text-text-primary">
        {isMobile ? (
          <div className="flex flex-col gap-2">
            <div className="truncate">{row.original.name}</div>
            <div className="text-text-tertiary">
              Last modified: {row.original.createdAt}
            </div>
          </div>
        ) : (
          row.original.name
        )}
      </span>
    </div>
  );
}

function FolderCellContent({
  cell,
  resetRowSelection,
  resetSorting,
  row,
}: {
  cell: Cell<FileItem, unknown>;
  resetRowSelection?: () => void;
  resetSorting?: () => void;
  row: Row<FileItem>;
}) {
  const isMobile = useIsMobileScreen();

  const href = row.original.type === "folder" ? row.original.href : undefined;
  let searchParams = {} as Record<string, number | string>;

  if (href === "sourcefiles" || href === "archive" || href === "trash") {
    searchParams = { folder: href };
  } else if (href === "requestType") {
    searchParams = { requestType: String(row.original.id) };
  } else if (href) {
    searchParams = { [`${href}Id`]: row.original.id };
  }

  return (
    <Link
      className="flex items-start md:items-center gap-lg "
      onClick={() => {
        resetSorting?.();
        resetRowSelection?.();
      }}
      search={(prev) => ({
        ...prev,
        ...searchParams,
      })}
      style={getColumnStyles(cell.column.id)}
      to="/my-files"
    >
      <FileIcon fileItem={row.original} />
      <span className="truncate text-xs md:text-sm text-text-primary">
        {isMobile ? (
          <div className="flex flex-col gap-2">
            <div className="truncate">{row.original.name}</div>
            <div className="text-text-tertiary">
              Last modified: {row.original.createdAt}
            </div>
          </div>
        ) : (
          row.original.name
        )}
      </span>
    </Link>
  );
}

function CellContent({
  cell,
  resetRowSelection,
  resetSorting,
  row,
}: {
  cell: Cell<FileItem, unknown>;
  resetRowSelection?: () => void;
  resetSorting?: () => void;
  row: Row<FileItem>;
}) {
  if (
    row.original.type === "folder" &&
    (cell.column.id === "name" || cell.column.id === "mobileName")
  ) {
    return (
      <FolderCellContent
        cell={cell}
        resetRowSelection={resetRowSelection}
        resetSorting={resetSorting}
        row={row}
      />
    );
  }

  if (cell.column.id === "name" || cell.column.id === "mobileName") {
    return <FileCellContent cell={cell} row={row} />;
  }

  if (cell.column.id === "size") {
    return (
      <span
        className="text-xs md:text-sm text-text-secondary"
        style={getColumnStyles(cell.column.id)}
      >
        {row.original.size === 0 ? "-" : prettyBytes(row.original.size)}
      </span>
    );
  }

  return (
    <span
      className="text-xs md:text-sm text-text-secondary"
      style={getColumnStyles(cell.column.id)}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </span>
  );
}

export function TableRow({
  resetRowSelection,
  resetSorting,
  row,
}: {
  resetRowSelection: () => void;
  resetSorting: () => void;
  row: Row<FileItem>;
}) {
  const href = row.original.type === "folder" ? row.original.href : undefined;

  return (
    <tr
      className={twMerge([
        "border-b border-border-secondary",
        row.getIsSelected() && "bg-bg-brand-primary",
        "hover:bg-bg-primary-hover",
      ])}
      key={row.id}
    >
      {row.getVisibleCells().map((cell) => (
        <td
          className="px-xl py-lg"
          key={cell.id}
          style={getColumnStyles(cell.column.id)}
        >
          <CellContent
            cell={cell}
            resetRowSelection={resetRowSelection}
            resetSorting={resetSorting}
            row={row}
          />
        </td>
      ))}
      <td
        className="px-xl py-lg"
        style={{ alignContent: "start", flex: "0 0 50px", width: "50px" }}
      >
        {href !== "trash" && (
          <ActionMenu
            handleResetRowSelection={resetRowSelection}
            href={href}
            isDirectory={row.original.isDirectory}
            itemId={row.original.id}
          />
        )}
      </td>
    </tr>
  );
}

export function TableHeader({ header }: { header: Header<FileItem, unknown> }) {
  return (
    <th
      className="px-xl py-md text-left"
      key={header.id}
      onClick={header.column.getToggleSortingHandler()}
      style={{
        cursor: header.column.getCanSort() ? "pointer" : "default",
        ...getColumnStyles(header.column.id),
      }}
    >
      <div className="flex items-center gap-1">
        {!header.isPlaceholder &&
          flexRender(header.column.columnDef.header, header.getContext())}
        <SortingIndicator header={header} />
      </div>
    </th>
  );
}
